import React, { useState, useMemo } from "react";
import { View, FlatList, Pressable, StyleSheet, ActivityIndicator, ScrollView } from "react-native";
import { ListTodo } from "lucide-react";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import { useRecentTodosQuery } from "../../../../graphql/generated/graphql";

export interface Todo {
  id: string;
  name: string;
  description?: string;
  date?: string | number | Date;
  completed?: boolean;
}

interface AddTaskModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectTask: (task: Todo) => void;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({ visible, onClose, onSelectTask }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { data, loading } = useRecentTodosQuery();

  const todos = useMemo(() => {
    if (!data?.recentTodos) return [];

    // Filter out completed todos and sort by date
    return data.recentTodos
      .filter((todo) => !todo.completed)
      .sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      })
      .slice(0, 15); // Show top 15 todos
  }, [data]);

  const filteredTodos = useMemo(() => {
    if (!searchQuery.trim()) return todos;

    const query = searchQuery.toLowerCase();
    return todos.filter(
      (todo) =>
        todo.name.toLowerCase().includes(query) || (todo.description && todo.description.toLowerCase().includes(query))
    );
  }, [todos, searchQuery]);

  const handleSelect = (todo: Todo) => {
    onSelectTask(todo);
    setSearchQuery("");
    onClose();
  };

  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const renderTodoItem = ({ item }: { item: Todo }) => {
    const isHovered = hoveredId === item.id;
    return (
      <Pressable
        style={[styles.todoItem, isHovered && styles.todoItemHovered]}
        onPress={() => handleSelect(item)}
        onMouseEnter={() => setHoveredId(item.id)}
        onMouseLeave={() => setHoveredId(null)}>
        <View style={styles.todoIcon}>
          <ListTodo size={20} color="#667085" />
        </View>
        <View style={styles.todoContent}>
          <CustomText text={item.name} useTranslationText={false} style={styles.todoName} numberOfLines={1} />
          {item.date && (
            <CustomText
              text={`Due ${new Date(item.date).toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}`}
              useTranslationText={false}
              style={styles.todoDate}
            />
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <CustomModal visible={visible} onRequestClose={onClose} title="Add Task" showCloseButton ariaLabel="Add Task Modal">
      <View style={styles.modalContent}>
        <CustomText
          text="Select a task from your to-do list to attach to your message"
          useTranslationText={false}
          style={styles.subtitle}
        />

        <CustomTextInput
          inputType="search"
          placeholder="Search tasks..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={styles.searchInput}
        />

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#3e68fe" />
          </View>
        ) : filteredTodos.length === 0 ? (
          <View style={styles.emptyContainer}>
            <CustomText
              text={searchQuery ? "No tasks found" : "No tasks available"}
              useTranslationText={false}
              style={styles.emptyText}
            />
          </View>
        ) : (
          <ScrollView style={styles.todoList} contentContainerStyle={styles.todoListContent}>
            {filteredTodos.map((item) => (
              <View key={item.id}>{renderTodoItem({ item })}</View>
            ))}
          </ScrollView>
        )}
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    width: "100%",
    maxWidth: 600,
    maxHeight: 600,
  },
  subtitle: {
    fontSize: 14,
    color: "#667085",
    marginBottom: 20,
  },
  searchInput: {
    marginBottom: 16,
  },
  todoList: {
    maxHeight: 400,
  },
  todoListContent: {
    paddingTop: 16,
    paddingBottom: 4,
  },
  todoItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 16,
    marginBottom: 8,
    cursor: "pointer",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  todoItemHovered: {
    backgroundColor: "#E6EBFF",
    borderColor: "#6788FE",
  },
  todoIcon: {
    marginRight: 12,
  },
  todoContent: {
    flex: 1,
  },
  todoName: {
    fontSize: 14,
    color: "#101828",
    fontWeight: "500",
    marginBottom: 2,
  },
  todoDate: {
    fontSize: 12,
    color: "#667085",
  },
  loadingContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: 14,
    color: "#667085",
  },
});
